import type { GameSnapshotPayload } from '@trames/shared/schemas';

/**
 * Buffer ordenado de snapshots (TKT-015).
 *
 * Inserción: O(log n) lógico, n=capacidad pequeña; con cap 32 amortizado O(1).
 * `push` descarta snapshots fuera de orden (tickId menor que el último).
 * `getInterpolationPair(targetTime)` devuelve los dos snapshots que envuelven
 * targetTime según `serverTime`, junto con el factor t en [0,1].
 *
 * Sin extrapolación: si targetTime > último.serverTime, devuelve null.
 */

export interface InterpolationPair {
  from: GameSnapshotPayload;
  to: GameSnapshotPayload;
  /** 0..1, fracción entre `from.serverTime` y `to.serverTime`. */
  t: number;
}

const MAX_BUFFER = 32;

export class SnapshotBuffer {
  private buf: GameSnapshotPayload[] = [];

  push(snap: GameSnapshotPayload): boolean {
    const last = this.buf[this.buf.length - 1];
    if (last && snap.tickId <= last.tickId) {
      // Fuera de orden o duplicado → descartar (no rompe el orden interno).
      return false;
    }
    this.buf.push(snap);
    if (this.buf.length > MAX_BUFFER) {
      this.buf.splice(0, this.buf.length - MAX_BUFFER);
    }
    return true;
  }

  /** Útil para tests/inspección. */
  size(): number {
    return this.buf.length;
  }

  newest(): GameSnapshotPayload | undefined {
    return this.buf[this.buf.length - 1];
  }

  getInterpolationPair(targetTime: number): InterpolationPair | null {
    if (this.buf.length < 2) return null;
    for (let i = this.buf.length - 1; i >= 1; i--) {
      const to = this.buf[i]!;
      const from = this.buf[i - 1]!;
      if (from.serverTime <= targetTime && targetTime <= to.serverTime) {
        const span = to.serverTime - from.serverTime;
        const t = span > 0 ? (targetTime - from.serverTime) / span : 1;
        return { from, to, t: Math.max(0, Math.min(1, t)) };
      }
    }
    return null;
  }
}
